import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, Truck, Palette, Star, CheckCircle, Download, FileText, Users, MapPin, Trophy, Phone } from 'lucide-react';
import { useSiteSeo } from '@/lib/useSiteSeo';
import { usePageMedia } from '@/lib/usePageMedia';
import GroupEnquiryForm from '@/components/groupbookings/GroupEnquiryForm';
import BrochureDownloadModal from '@/components/corporate/BrochureDownloadModal';

const SERVICE_PILLARS = [
  {
    icon: Building2,
    title: 'EXCLUSIVE VENUE HIRE',
    desc: 'Take over our entire Bromborough facility for staff parties, client networking, and private corporate events. Full access to professional-grade motion simulators with dedicated event staff who run every session.',
    points: ['Private facility access', 'Dedicated event coordinator', 'Catering & hospitality options', 'Up to 30 simultaneous drivers'],
  },
  {
    icon: Truck,
    title: 'MOBILE SIMULATOR HIRE',
    desc: "We bring the rigs to you. Professional motion sim racing setups delivered and installed at trade shows, exhibitions, office parties, and product launches across the UK — fully managed by our technical team.",
    points: ['Up to 4 mobile rigs', 'Nationwide delivery & setup', 'On-site technical support', 'Branded rig wrapping available'],
  },
  {
    icon: Palette,
    title: 'BRAND ACTIVATIONS',
    desc: 'Custom in-game car branding, liveries, and lead-generation integrations. Turn sim racing into a powerful marketing tool that captures attention at your event and drives qualified leads for your sales team.',
    points: ['Custom in-game car liveries', 'Lead capture integrations', 'Social media content creation', 'Live leaderboard sponsorship'],
  },
];

const TRUST_LOGOS = [
  'YOUR LOGO HERE',
  'YOUR LOGO HERE',
  'YOUR LOGO HERE',
  'YOUR LOGO HERE',
  'YOUR LOGO HERE',
  'YOUR LOGO HERE',
];

const TESTIMONIALS = [
  {
    quote: 'Motion Reality hosted our annual team building event and it was incredible. The simulators are professional-grade and the staff made sure everyone had a brilliant time, from complete beginners to seasoned racers.',
    author: 'Event Manager',
    company: 'Merseyside Tech Firm',
  },
  {
    quote: 'We hired their mobile rig setup for our exhibition stand at the NEC. It drew massive crowds all weekend and the branded in-game cars were a brilliant touch. Highly recommend for any brand activation.',
    author: 'Marketing Director',
    company: 'Automotive Client',
  },
];

const STATS = [
  { icon: Users, value: '30+', label: 'SIMULTANEOUS DRIVERS' },
  { icon: MapPin, value: 'UK-WIDE', label: 'MOBILE COVERAGE' },
  { icon: Trophy, value: '100%', label: 'CUSTOM BRANDING' },
];

export default function Corporate() {
  const formRef = useRef(null);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const { get: getPageMedia } = usePageMedia();

  const heroImg = getPageMedia('corporate', 'hero');
  const brochureUrl = getPageMedia('corporate', 'brochure_pdf');

  useSiteSeo('corporate', {
    title: 'Corporate Events & Team Building | Motion Reality Sim Racing',
    description: 'Unforgettable corporate events and team building at Motion Reality. Exclusive venue hire in Bromborough, mobile simulator hire for trade shows, and custom brand activations with in-game car branding.',
  });

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-[#0d0d0f] min-h-screen">
      {/* ── HERO (Split-Screen) ── */}
      <section className="relative min-h-[70vh] lg:min-h-[80vh] flex items-stretch overflow-hidden border-b border-white/5">
        {/* Left content panel */}
        <div className="relative w-full lg:w-1/2 flex items-center bg-[#0d0d0f] px-6 lg:px-10 py-16 lg:py-0 z-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-lg">
            <p className="text-primary text-xs font-bold tracking-[0.3em] mb-3">CORPORATE & B2B</p>
            <h1 className="text-white font-black uppercase leading-[0.9] mb-5" style={{ fontSize: 'clamp(1.75rem, 4vw, 3.5rem)' }}>
              Unforgettable Corporate<br />Events &amp; Team Building
            </h1>
            <p className="text-white/50 text-sm md:text-base max-w-md mb-8 leading-relaxed">
              From exclusive venue hire to mobile simulator setups and brand activations, we deliver high-octane corporate experiences that your team and clients will never forget.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={scrollToForm}
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest px-8 py-3.5 transition-colors"
              >
                REQUEST A CORPORATE QUOTE <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setBrochureOpen(true)}
                className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-primary/50 text-white/70 hover:text-primary font-bold text-sm tracking-widest px-8 py-3.5 transition-colors"
              >
                <Download className="w-4 h-4" /> DOWNLOAD BROCHURE
              </button>
            </div>
          </motion.div>
        </div>

        {/* Right image panel */}
        <div className="hidden lg:block w-1/2 relative">
          <img
            src={heroImg}
            alt="Corporate sim racing event at Motion Reality"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0f] via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0f]/60 via-transparent to-transparent" />
        </div>

        {/* Mobile image background */}
        <div className="lg:hidden absolute inset-0 -z-0">
          <img src={heroImg} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0f]/80 to-[#0d0d0f]" />
        </div>
      </section>

      {/* ── STATS STRIP ── */}
      <section className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-3 divide-x divide-white/5">
          {STATS.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="flex flex-col sm:flex-row items-center justify-center gap-3 py-6 text-center">
                <Icon className="w-5 h-5 text-primary shrink-0" />
                <div>
                  <p className="text-white font-black text-base sm:text-lg leading-none">{stat.value}</p>
                  <p className="text-white/30 text-[9px] font-bold tracking-widest mt-1">{stat.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── SERVICE PILLARS ── */}
      <section className="py-16 lg:py-20 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <p className="text-primary text-xs font-bold tracking-widest mb-2">WHAT WE OFFER</p>
            <h2 className="text-white font-black text-2xl md:text-4xl uppercase leading-tight">B2B Service Pillars</h2>
            <p className="text-white/40 text-sm max-w-xl mx-auto mt-3 leading-relaxed">
              Three ways to put your brand and your people at the heart of the action.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {SERVICE_PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="relative border border-white/5 bg-[#111115] p-7 hover:border-primary/30 hover:bg-[#141419] transition-all group flex flex-col"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-primary/10 border border-primary/20 mb-6 group-hover:bg-primary/20 transition-colors">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-white font-black text-sm tracking-wide mb-3">{pillar.title}</h3>
                  <p className="text-white/40 text-xs leading-relaxed mb-6">{pillar.desc}</p>
                  <ul className="space-y-2 mt-auto">
                    {pillar.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-xs text-white/50">
                        <CheckCircle className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TRUSTED BY ── */}
      <section className="py-16 lg:py-20 px-6 lg:px-10 bg-[#111115] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 text-center">
            <p className="text-primary text-xs font-bold tracking-widest mb-2">PROVEN TRACK RECORD</p>
            <h2 className="text-white font-black text-2xl md:text-4xl uppercase leading-tight">Trusted By</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {TRUST_LOGOS.map((logo, i) => (
              <div key={i} className="aspect-[3/2] border border-white/5 bg-[#0d0d0f] flex items-center justify-center hover:border-primary/20 transition-colors">
                <span className="text-white/15 text-[10px] font-bold tracking-widest text-center px-2">{logo}</span>
              </div>
            ))}
          </div>
          <p className="text-white/20 text-xs text-center mt-4">Logo carousel placeholder — ready for client logo uploads.</p>
        </div>
      </section>

      {/* ── B2B TESTIMONIALS ── */}
      <section className="py-16 lg:py-20 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <p className="text-primary text-xs font-bold tracking-widest mb-2">CLIENT FEEDBACK</p>
            <h2 className="text-white font-black text-2xl md:text-4xl uppercase leading-tight">What Our Clients Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="border border-white/5 bg-[#111115] p-7 flex flex-col"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-primary fill-primary" />
                  ))}
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-6 italic flex-1">&ldquo;{t.quote}&rdquo;</p>
                <div className="border-t border-white/5 pt-4">
                  <p className="text-white font-bold text-xs">{t.author}</p>
                  <p className="text-white/30 text-xs">{t.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LEAD FORM + BROCHURE CTA ── */}
      <section ref={formRef} className="py-16 lg:py-20 px-6 lg:px-10 bg-[#111115] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
            <div className="lg:col-span-2 text-center lg:text-left">
              <p className="text-primary text-xs font-bold tracking-widest mb-2">GET STARTED</p>
              <h2 className="text-white font-black text-2xl md:text-4xl uppercase leading-tight mb-3">Request a Corporate Quote</h2>
              <p className="text-white/40 text-sm max-w-xl leading-relaxed">
                Tell us about your event and we'll put together a tailored package. Our team responds within 24 hours.
              </p>
            </div>
            {/* Brochure CTA card */}
            <div className="border border-primary/20 bg-primary/5 p-6 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="w-5 h-5 text-primary shrink-0" />
                <p className="text-white font-bold text-sm">Corporate Brochure</p>
              </div>
              <p className="text-white/40 text-xs leading-relaxed mb-4">
                Want the full pricing guide and package tiers? Download our corporate brochure instantly.
              </p>
              <button
                onClick={() => setBrochureOpen(true)}
                className="inline-flex items-center justify-center gap-2 border border-primary/40 hover:bg-primary hover:border-primary text-primary hover:text-white font-bold text-xs tracking-widest py-3 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> DOWNLOAD BROCHURE
              </button>
            </div>
          </div>

          <GroupEnquiryForm type="corporate" />

          {/* Direct contact fallback */}
          <div className="mt-8 flex items-center justify-center gap-2 text-white/30 text-xs">
            <Phone className="w-3.5 h-3.5" />
            Prefer to talk now? Call us on{' '}
            <a href="tel:01515283001" className="text-primary font-bold hover:underline">0151 528 3001</a>
          </div>
        </div>
      </section>

      {/* ── BROCHURE MODAL ── */}
      <BrochureDownloadModal
        open={brochureOpen}
        onClose={() => setBrochureOpen(false)}
        pdfUrl={brochureUrl}
      />
    </div>
  );
}